import { ApiError } from './http'

/**
 * Машинные коды доменных отказов → текст для человека. Коды приходят
 * UPPER_SNAKE из `mapErr` каждого домена бэкенда.
 *
 * Свой текст держим не потому, что серверный плох (он на русском и осмыслен), а
 * потому что интерфейс говорит подробнее: у него есть контекст экрана.
 * Незнакомый код падает на серверный `detail` — новый отказ бэкенда покажется
 * человеку осмысленно, а не «что-то пошло не так».
 */
const MESSAGES: Record<string, string> = {
  // employee
  INVALID_CREDENTIALS: 'Логин или пароль не подошли',
  EMPLOYEE_NOT_FOUND: 'Сотрудник не найден',
  LOGIN_TAKEN: 'Такой логин уже занят',
  WEAK_PASSWORD: 'Пароль слишком простой — возьмите длиннее и не похожий на логин',
  INVALID_LOGIN: 'Логин не годится: без пробелов, латиницей',
  EMPLOYEE_INACTIVE: 'Сотрудник отключён',
  ALREADY_IN_STATE: 'Сотрудник уже в этом состоянии',
  LINK_STATE_INVALID: 'Ссылка привязки истекла или уже использована — начните заново',
  CHAT_ALREADY_LINKED: 'Этот телеграм привязан к другому сотруднику',
  NOT_LINKED: 'Телеграм не привязан',

  // schedule
  TASK_NOT_FOUND: 'Дело не найдено — возможно, его уже удалили',
  EMPTY_TITLE: 'Название обязательно',
  INVALID_SLOT: 'Конец интервала должен быть позже начала',
  INVALID_PERIOD: 'Период задан неверно',
  DUPLICATE_ASSIGNEE: 'Один и тот же человек назначен дважды',
  SLOT_ALREADY_EMPTY: 'У этого дела и так нет времени',
  ASSIGNEE_NOT_FOUND: 'Этот человек не исполнитель дела',
  EXCURSION_ALREADY_ATTACHED: 'Дело уже привязано к другому выходу',
  ALREADY_COMPLETED: 'Дело уже закрыто',
  NOT_COMPLETED: 'Дело не закрыто',

  // excursion
  LINK_INVALID: 'Ссылка не действует',
  EXCURSION_NOT_FOUND: 'Выход не найден',
  EXCURSION_NOT_BOOKABLE: 'На этот выход записаться уже нельзя',
  EXCURSION_FINISHED: 'Выход уже отменён или состоялся — менять его нельзя',
  BOOKING_NOT_FOUND: 'Запись не найдена',
  BOOKING_ALREADY_CANCELLED: 'Запись уже снята',
  ALREADY_BOOKED: 'Такая запись на этот выход уже есть',
  GUIDE_NOT_ASSIGNED: 'Этот человек не ведёт выход',
  DUPLICATE_GUIDE: 'Один и тот же гид указан дважды',
  CONSENT_REQUIRED: 'Без согласия на обработку данных записать не получится',
  INVALID_PHONE: 'Телефон не похож на российский номер',
  INVALID_SEATS: 'Количество мест должно быть больше нуля',
  INVALID_CAPACITY: 'Вместимость должна быть больше нуля',
  EMPTY_REASON: 'Нужна причина — её увидит и команда, и тот, кто откроет ссылку',
  NOTHING_REQUESTED: 'Нужен период или конкретный выход',
  INVALID_STATE: 'Неизвестное состояние выхода',

  // client
  CLIENT_NOT_FOUND: 'Клиент не найден',
  CLIENT_FORGOTTEN: 'Данные клиента стёрты — их нельзя менять',
  ALREADY_FORGOTTEN: 'Данные клиента уже стёрты',
  ALREADY_MERGED: 'Этот контакт уже объединён с другим',
  MERGE_INTO_ITSELF: 'Нельзя объединить контакт сам с собой',
  EMPTY_NAME: 'Имя обязательно',
  CONSENT_WORDING_REQUIRED: 'Нужен текст, которым брали согласие',
  INVALID_CONSENT_PURPOSE: 'Неизвестное назначение согласия',
  INVALID_CONSENT_SOURCE: 'Неизвестный источник согласия',
  NO_ACTIVE_CONSENT: 'Действующего согласия нет',
  INVALID_PAYLOAD: 'В запросе не хватает данных',

  // общее для всех доменов
  CONCURRENT_UPDATE: 'Кто-то изменил это одновременно с вами — обновите страницу',
}

const FALLBACK = 'Что-то пошло не так. Попробуйте ещё раз'

/** Текст для человека по любой пойманной ошибке. */
export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return FALLBACK
  const known = MESSAGES[error.code]
  if (known) return known
  // 5xx наружу не пересказываем: там внутренности, а не объяснение.
  if (error.status >= 500) return FALLBACK
  return error.detail || FALLBACK
}

/** Код отказа, если он доменный; иначе undefined. */
export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError && error.code ? error.code : undefined
}
